// Import the Angular Component decorator and OnInit lifecycle interface.
import { Component, OnInit } from '@angular/core';

// Define the Angular component metadata for the Group Training Organisation page.
@Component({
  // Register the HTML selector used for this component.
  selector: 'app-gto',
  // Link the component to its HTML template file.
  templateUrl: './gto.component.html',
  // Link the component to its component-specific SCSS file.
  styleUrls: ['./gto.component.scss']
})
// Export the Group Training Organisation component and use the standard OnInit structure used by the project.
export class GtoComponent implements OnInit {
  // Store the three steps used to explain how the ECB Group Training Organisation model works.
  readonly steps = [
    // Define Step 1 where ECB becomes the apprentice or trainee's legal employer.
    {
      // Display the first step number.
      number: '01',
      // Display the first step heading.
      title: 'ECB Employs You',
      // Display the first step description from the supplied website brief.
      text: 'ECB Training Services signs your training contract and becomes your legal employer. We pay your wages, manage your leave entitlements, and handle all your employment paperwork. You have all the rights of a regular employee.'
    },
    // Define Step 2 where ECB places the apprentice or trainee with a suitable host employer.
    {
      // Display the second step number.
      number: '02',
      // Display the second step heading.
      title: 'We Place You With a Host',
      // Display the second step description from the supplied website brief.
      text: 'We match you with a host employer — a business in your industry where you will work and learn. You go to that workplace every day, build real skills, and gain real experience. If your host placement ends, we find you another one — you never lose your job.'
    },
    // Define Step 3 where employment and nationally recognised training happen together.
    {
      // Display the third step number.
      number: '03',
      // Display the third step heading.
      title: 'You Earn While You Learn',
      // Display the third step description from the supplied website brief.
      text: 'While you work, you are also studying toward a nationally recognised qualification through ECB Training Services as a registered RTO. You earn a wage and gain a qualification at the same time.'
    }
  ];

  // Store the reasons that explain the benefits of choosing ECB as a Group Training Organisation.
  readonly reasons = [
    // Explain ECB's First Nations-led and Supply Nation certified position.
    'First Nations-led — ECB is an Aboriginal-owned, Supply Nation certified business. We understand the communities we serve.',
    // Explain the benefit of ECB also operating as registered RTO 41369.
    'Already a registered RTO (41369) — your training and qualification are delivered by the same organisation that employs you. No handovers, no gaps.',
    // Explain ECB's Group Training Organisation approval coverage.
    'Also a Group Training Organisation — ECB has GTO approval across the Northern Territory and Victoria.',
    // Explain ECB's existing employer network across multiple industries.
    'Strong employer networks — ECB has established relationships with Darwin employers across construction, resources, hospitality, and community services.',
    // Explain the ongoing support available from an ECB placement coordinator.
    'Dedicated support — you have a placement coordinator alongside you throughout your apprenticeship or traineeship, not just at the start.',
    // Explain the culturally safe support provided by ECB.
    'Cultural safety — we understand what it means to be First Nations in the workplace and provide culturally safe support.'
  ];

  // Store the benefits displayed for apprentices and trainees.
  readonly apprenticeBenefits = [
    // Explain that participants are paid from their first day of employment.
    'You receive a real wage from Day 1',
    // Explain that participants work toward a nationally recognised qualification.
    'You work toward a nationally recognised qualification',
    // Explain that participants retain normal employment rights and entitlements.
    'You have full employment rights and entitlements',
    // Explain the ongoing placement coordinator support available throughout the program.
    'ECB stays with you for the full duration — your placement coordinator is one call away',
    // Explain that ECB can arrange another placement if the original host placement ends.
    'If your host employer can no longer take you, ECB finds you a new placement — your employment continues'
  ];

  // Store the benefits displayed for businesses that become host employers.
  readonly employerBenefits = [
    // Explain that ECB manages the formal employer administration and obligations.
    'ECB handles all employment paperwork, wages, superannuation, and industrial relations',
    // Explain that host employers receive candidates matched to their industry.
    'You get a work-ready candidate matched to your industry',
    // Explain the placement coordinator support provided to host employers.
    'ECB\'s placement coordinator manages any issues — you have direct support',
    // Explain that host arrangements can be flexible in duration.
    'Flexible arrangements — host for a short period or the full apprenticeship',
    // Explain that eligible host employers may have access to government incentives.
    'Government incentives may be available for eligible host employers'
  ];

  // Keep page initialisation in a dedicated method to match the existing component structure used in the project.
  loadPageData(): void {
    // This page currently uses static content from the approved brief, so no API call is required here.
  }

  // Run the component initialisation logic when Angular creates the page.
  ngOnInit(): void {
    // Load any data required by the page.
    this.loadPageData();
  }
}
